import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Loader2,
  FileSignature,
  Lock,
  CheckCircle2,
  Clock,
  Copy,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getContractOrderByToken } from "@/lib/api/contract-orders.functions";
import { site } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Disclaimer } from "@/components/site/Disclaimer";
import { CONTRACT_TEMPLATES_META } from "@/lib/contract-templates";
import { PayFastCheckoutButton } from "@/components/site/PayFastCheckoutButton";

export const Route = createFileRoute("/contracts/$slug/order/$token")({
  loader: ({ params }) => {
    const tpl = CONTRACT_TEMPLATES_META.find((t) => t.slug === params.slug);
    if (!tpl) throw notFound();
    return { tpl, token: params.token };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.tpl.title ?? "Order"} — payment & download`,
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center">
      <p className="text-muted-foreground">Order not found.</p>
      <Link to="/contracts" className="mt-4 inline-block text-navy underline">
        Back to contract library
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-narrow py-24 text-center">
      <p className="text-muted-foreground">Could not load order: {error.message}</p>
    </div>
  ),
  component: OrderPage,
});

function copy(text: string) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Copied"),
    () => toast.error("Copy failed"),
  );
}

function OrderPage() {
  const { tpl, token } = Route.useLoaderData();
  const fetchOrder = useServerFn(getContractOrderByToken);
  const qc = useQueryClient();
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["contract-order", token],
    queryFn: () => fetchOrder({ data: { token } }),
    // Poll fast while pending so the buyer sees the unlock the moment admin
    // marks paid. Once paid, stop polling.
    refetchInterval: (q) =>
      (q.state.data as any)?.order?.status === "paid" ? false : 5_000,
    refetchOnWindowFocus: true,
  });


  async function downloadPaid() {
    setDownloading(true);
    try {
      const res = await fetch("/api/public/contract-personalised", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: tpl.slug, token }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Server returned ${res.status}`);
      }
      const blob = await res.blob();
      const contentType = res.headers.get("content-type") || "";
      const ext = contentType.includes("zip") ? "zip" : "pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        ext === "zip" ? `${tpl.slug}-LOCKED.zip` : `${tpl.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        ext === "zip"
          ? "Locked archive downloaded — open with the password we send you"
          : "Download started",
      );
    } catch (e: unknown) {
      toast.error("Download failed", {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setDownloading(false);
    }
  }


  if (isLoading) {
    return (
      <div className="container-narrow py-20 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin" />
        Loading order…
      </div>
    );
  }
  if (error || !data?.order) {
    return (
      <div className="container-narrow py-20 text-center text-sm text-red-600">
        Order not found. Please check your link.
      </div>
    );
  }

  const order = data.order;
  const paid = order.status === "paid";
  const cancelled = order.status === "cancelled";

  return (
    <div className="bg-background">
      <section className="bg-gradient-to-b from-navy/5 to-transparent py-12">
        <div className="container-narrow">
          <Link
            to="/contracts"
            className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to contracts
          </Link>
          <div className="mt-4 flex items-start gap-4">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-md ${
                paid ? "bg-emerald-100 text-emerald-700" : "bg-navy/10 text-navy"
              }`}
            >
              {paid ? <CheckCircle2 className="h-6 w-6" /> : <Lock className="h-6 w-6" />}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">
                Premium contract
              </p>
              <h1 className="mt-1 font-display text-3xl text-navy md:text-4xl">
                {order.title}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Reference{" "}
                <span className="font-mono font-semibold text-navy">
                  {order.reference}
                </span>{" "}
                · R{order.amount_zar}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-narrow grid gap-6 md:grid-cols-2">
          {paid ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 shadow-soft md:col-span-2">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-6 w-6 text-emerald-700" />
                <div className="flex-1">
                  <h2 className="font-display text-xl text-navy">
                    Payment confirmed — your document is ready
                  </h2>
                  <p className="mt-1 text-sm text-foreground/80">
                    Your branded document has been emailed and SMSed with the
                    unlock password. Re-download the locked archive below if
                    you need a fresh copy.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button
                      onClick={downloadPaid}
                      disabled={downloading}
                      className="bg-navy hover:bg-navy/90"
                    >
                      {downloading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Preparing…
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Re-download locked ZIP
                        </>
                      )}
                    </Button>

                    <a
                      href={`/api/public/receipt?token=${encodeURIComponent(token)}`}
                      className="inline-flex items-center justify-center rounded-md border border-emerald-700 bg-white px-4 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download payment receipt
                    </a>
                  </div>
                  <p className="mt-3 text-xs text-emerald-900/70">
                    Reference {order.reference} · paid R{order.amount_zar}
                  </p>
                </div>
              </div>
            </div>

          ) : cancelled ? (
            <div className="rounded-2xl border border-border bg-card p-6 md:col-span-2">
              <h2 className="font-display text-xl text-navy">Order cancelled</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                This order has been cancelled. Please{" "}
                <Link to="/contracts" className="text-navy underline">
                  start a new request
                </Link>{" "}
                if you still need the contract.
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-gold" />
                  <h2 className="font-display text-xl text-navy">
                    Manual EFT (fallback only)
                  </h2>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  We recommend paying with PayFast above — your contract unlocks
                  the instant PayFast confirms payment. If you'd rather pay by
                  manual EFT, use the banking details below. We'll mark this
                  order paid as soon as we see your transfer.
                </p>
                <dl className="mt-4 divide-y divide-border text-sm">
                  {[
                    ["Account name", site.banking.accountName],
                    ["Bank", site.banking.bank],
                    ["Account number", site.banking.accountNumber],
                    ["Branch code", site.banking.branchCode],
                    ["Amount", `R${order.amount_zar}.00`],
                    ["Reference (use this)", order.reference],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-3 py-2">
                      <dt className="text-muted-foreground">{k}</dt>
                      <dd className="flex items-center gap-2 text-right font-medium text-navy">
                        <span className="font-mono">{v}</span>
                        <button
                          type="button"
                          onClick={() => copy(String(v))}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-navy"
                          aria-label={`Copy ${k}`}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={`/api/public/receipt?token=${encodeURIComponent(token)}`}
                    className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy/90"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download branded invoice (PDF)
                  </a>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  Already paid? Email proof of payment to{" "}
                  <a className="underline" href={`mailto:${site.email}`}>
                    {site.email}
                  </a>{" "}
                  or WhatsApp{" "}
                  <a
                    className="underline"
                    href={`https://wa.me/${site.whatsapp}?text=${encodeURIComponent(
                      `Hi, POP for ${order.reference}`,
                    )}`}
                  >
                    {site.phone}
                  </a>
                  .
                </p>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <h2 className="font-display text-xl text-navy">Status</h2>
                <p className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
                  <Clock className="h-3.5 w-3.5" /> Awaiting payment
                </p>
                <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <p>
                    <span className="text-foreground">Client:</span>{" "}
                    {order.client_name}
                  </p>
                  <p>
                    <span className="text-foreground">Submitted:</span>{" "}
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>

                <div className="mt-5 rounded-lg border border-dashed border-navy/30 bg-navy/5 p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-navy">
                    <Lock className="h-4 w-4" /> Download the locked archive now
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your branded document is ready, wrapped in an AES-256
                    password-protected ZIP. We email and SMS you the unlock
                    password the moment we confirm your EFT.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={downloadPaid}
                      disabled={downloading}
                      className="bg-navy hover:bg-navy/90"
                    >
                      {downloading ? (
                        <>
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          Preparing…
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-3.5 w-3.5" />
                          Download locked ZIP
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        qc.invalidateQueries({ queryKey: ["contract-order", token] })
                      }
                    >
                      Refresh status
                    </Button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <h3 className="font-display text-lg text-navy">
                    Pay securely with PayFast
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Pay R{order.amount_zar} by card, Instant EFT, SnapScan,
                    Zapper or PayFast wallet. Your contract unlocks automatically
                    the moment PayFast confirms payment — no waiting for manual
                    review.
                  </p>
                  <div className="mt-4">
                    <PayFastCheckoutButton
                      reference={order.reference}
                      label={`Pay R${order.amount_zar} with PayFast`}
                    />
                  </div>
                </div>
              </div>
            </>

          )}

          <div className="md:col-span-2">
            <Disclaimer variant="compact" />
          </div>
        </div>
      </section>
    </div>
  );
}
